import { Context } from '@/context/appContext';
import { getResponseError } from '@/helpers';
import { BASE_TARIFF, calcDiscount, CURRENCY_LABEL, getBaseCost, useBillingInfoGetter } from '@/helpers/payments';
import { useNotifications } from '@/hooks/useNotifications';
import { AppStateActions } from '@/reducers/appReducer';
import { CloseIcon } from '@/icons';
import {
  AppBar,
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Toolbar,
} from '@mui/material';
import { getSubscriptionPlans, SubscriptionPlanDto, SubscriptionType } from '@tailored-pdf/api';
import { ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { PaymentForm } from './PaymentForm';
import { Header } from './styles';
import { ROUTES } from '@/constants/constants';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/useIsMobile';

function RadioLabel({
  tariff,
  plans,
  label,
}: {
  label: string;
  tariff: SubscriptionType;
  plans?: SubscriptionPlanDto[];
}) {
  const { t: tt } = useTranslation('web');
  const plan = plans?.find((v) => v.subscriptionType === tariff);

  if (!plan) {
    return null;
  }

  const baseCost = getBaseCost(plan);
  const discount = calcDiscount(tariff);
  const periodName =
    plan && plan.subscriptionType == SubscriptionType.TRIAL
      ? `${CURRENCY_LABEL}${baseCost}`
      : `${CURRENCY_LABEL}${baseCost}/${tt(`tariffs.basePaymentPeriod`)}`;

  return baseCost !== undefined ? (
    <Box display={'flex'} flex={'1 1 auto'} justifyContent={'space-between'}>
      <Box display={'flex'} gap={1} alignItems={'center'}>
        <span>
          <b>{label}</b>
        </span>
        {discount ? <Chip size="small" label={`-${discount}%`} color="success" /> : null}
      </Box>
      <Box sx={{ color: 'text.primary' }}>{periodName}</Box>
    </Box>
  ) : null;
}

const TARIFF_TYPES = [SubscriptionType.TRIAL, SubscriptionType.MONTHLY, SubscriptionType.YEARLY];

type TariffSelectorProps = {
  tariff: SubscriptionType;
  subscriptionPlans?: SubscriptionPlanDto[];
  onTariffChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

function TariffSelector({ tariff, subscriptionPlans, onTariffChange }: TariffSelectorProps) {
  const { t: tt } = useTranslation('web');

  return (
    <RadioGroup value={tariff} onChange={onTariffChange}>
      {TARIFF_TYPES.map((type) => (
        <FormControlLabel
          key={type}
          disableTypography
          sx={tariff === type ? { color: 'primary.main' } : {}}
          value={type}
          control={<Radio />}
          label={
            <RadioLabel
              label={tt(`tariffs.names.${type}`)}
              tariff={type}
              plans={subscriptionPlans}
            />
          }
        />
      ))}
    </RadioGroup>
  );
}

type LayoutProps = TariffSelectorProps & {
  uploadedFileId?: string | null;
  billingInfoContent: ReactNode;
  onSubscriptionSuccess: () => void;
  onCancel: () => void;
  onSubmittingChange: (submitting: boolean) => void;
};

function MobileLayout({
  tariff,
  subscriptionPlans,
  uploadedFileId,
  billingInfoContent,
  onTariffChange,
  onSubscriptionSuccess,
  onCancel,
  onSubmittingChange,
}: LayoutProps) {
  return (
    <Grid container spacing={8}>
      <Grid item sm={12} sx={{ width: '100%' }}>
        <Card variant="outlined" sx={{ width: '100%' }}>
          <CardContent sx={{ width: '100% !important' }}>
            <TariffSelector tariff={tariff} subscriptionPlans={subscriptionPlans} onTariffChange={onTariffChange} />
          </CardContent>
          <Divider />
        </Card>
      </Grid>
      <Grid item sm={12}>
        <PaymentForm
          documentId={uploadedFileId || undefined}
          subscriptionType={tariff}
          onSubscriptionSuccess={onSubscriptionSuccess}
          onCancel={onCancel}
          onSubmittingChange={onSubmittingChange}
          billingInfo={billingInfoContent}
        />
      </Grid>
    </Grid>
  );
}

function DesktopLayout({
  tariff,
  subscriptionPlans,
  uploadedFileId,
  billingInfoContent,
  onTariffChange,
  onSubscriptionSuccess,
  onCancel,
  onSubmittingChange,
}: LayoutProps) {
  return (
    <Grid container spacing={8}>
      <Grid item sm={12} md={5}>
        <Card variant="outlined">
          <CardContent>
            <TariffSelector tariff={tariff} subscriptionPlans={subscriptionPlans} onTariffChange={onTariffChange} />
          </CardContent>
          <Divider />
          <CardContent>{billingInfoContent}</CardContent>
        </Card>
      </Grid>
      <Grid item sm={12} md={7}>
        <PaymentForm
          documentId={uploadedFileId || undefined}
          subscriptionType={tariff}
          onSubscriptionSuccess={onSubscriptionSuccess}
          onCancel={onCancel}
          onSubmittingChange={onSubmittingChange}
        />
      </Grid>
    </Grid>
  );
}

function PaymentModal() {
  const { t, i18n } = useTranslation('web', { keyPrefix: 'components.paymentModal' });
  const { t: tt } = useTranslation('web');
  const isMobile = useIsMobile();
  const {
    state: { uploadedFileId, preselectedTariff, subscriptionPlans, userInfo },
    dispatch,
  } = useContext(Context);
  const navigate = useNavigate();

  const { notifyError } = useNotifications();
  const getBillingInfo = useBillingInfoGetter();

  const [tariff, setTariff] = useState(BASE_TARIFF);
  const [submitting, setSubmitting] = useState(false);

  const activePlan = subscriptionPlans?.find((v) => v.subscriptionType === tariff);

  const loadPlans = useCallback(async () => {
    try {
      const resp = await getSubscriptionPlans();
      dispatch({
        type: AppStateActions.SET_SUBSCRIPTION_PLANS,
        payload: { subscriptionPlans: resp.data },
      });
    } catch (e) {
      notifyError(getResponseError(e));
    }
  }, [dispatch, notifyError]);

  const handleClose = useCallback(
    () =>
      dispatch({
        type: AppStateActions.SET_NEED_PAYMENT,
        payload: { needPayment: false, preselectedTariff: undefined },
      }),
    [dispatch],
  );

  const handleSubscribed = useCallback(() => {
    dispatch({
      type: AppStateActions.SET_LAST_PAYMENT_DATE,
      payload: { lastPaymentDate: new Date().toLocaleString() },
    });
    if (userInfo) {
      dispatch({
        type: AppStateActions.SET_USER_INFO,
        payload: { userInfo: { ...userInfo, subscriptionType: tariff } },
      });
    }
    handleClose();
    if (uploadedFileId && location.pathname !== ROUTES.convertToWord(i18n.language)) {
      uploadedFileId && navigate(ROUTES.results(i18n.language, uploadedFileId));
    }
  }, [dispatch, handleClose, i18n.language, navigate, uploadedFileId, userInfo]);

  const handleChangeTariff = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setTariff(event.target.value as SubscriptionType);
  }, []);

  useEffect(() => {
    setTariff(preselectedTariff ?? BASE_TARIFF);
  }, [preselectedTariff]);

  useEffect(() => {
    void loadPlans();
  }, [loadPlans]);

  const billingInfoContent = (
    <>
      <Box fontWeight={600} marginBottom={2}>
        {activePlan &&
          `${t('billed')} ${CURRENCY_LABEL}${activePlan.price}/${tt(
            `tariffs.period.${activePlan.subscriptionType}`,
          )}`}
      </Box>
      <Box sx={{ color: 'text.disabled' }} fontSize={12}>
        {getBillingInfo(tariff, subscriptionPlans)}{' '}
        <Trans
          t={t}
          i18nKey="cancelation"
          components={{
            link: (
              <a href="mailto:support@tailored-pdf.com">
                <Box fontSize={'inherit'} color={'primary.main'} display={'inline-flex'}>
                  support@tailored-pdf.com
                </Box>
              </a>
            ),
          }}
        />
      </Box>
    </>
  );

  const layoutProps: LayoutProps = {
    tariff,
    subscriptionPlans,
    uploadedFileId,
    billingInfoContent,
    onTariffChange: handleChangeTariff,
    onSubscriptionSuccess: handleSubscribed,
    onCancel: handleClose,
    onSubmittingChange: setSubmitting,
  };

  const Layout = isMobile ? MobileLayout : DesktopLayout;

  return (
    <Dialog
      open
      maxWidth={'lg'}
      fullWidth
      fullScreen={isMobile}
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        // Only the Cancel/close button may dismiss the modal — ignore backdrop
        // clicks (escape is disabled) to avoid accidental drop-off and hurting conversion.
        if (reason === 'backdropClick') return;
        // Closing unmounts <Elements> and destroys the PaymentElement; refuse
        // while a payment is in flight to avoid Stripe "Element already destroyed" errors.
        if (submitting) return;
        handleClose();
      }}
    >
      {isMobile && (
        <AppBar position="static" color="transparent" elevation={0}>
          <Toolbar sx={{ justifyContent: 'flex-end', minHeight: '0 !important' }}>
            <IconButton onClick={handleClose} disabled={submitting} edge="end" aria-label="close">
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}
      <Box display={'flex'} flexDirection={'column'} paddingX={8} paddingY={isMobile ? 0 : 8} flex={'1 1 auto'}>
        <Header>
          <Trans
            t={t}
            i18nKey="title"
            components={{
              accent: <Box color={'primary.main'} display={'inline-flex'} />,
            }}
          />
        </Header>

        <Layout {...layoutProps} />
      </Box>
    </Dialog>
  );
}

export { PaymentModal };
